import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { FieldArrayType } from '@ngx-formly/core';
import { isEqual } from 'lodash-es';
import { Observable, Subject, combineLatest } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, take, takeUntil, tap, withLatestFrom } from 'rxjs/operators';

import { ProductContextDirective } from 'ish-core/directives/product-context.directive';
import { AppFacade } from 'ish-core/facades/app.facade';
import { ProductContextFacade } from 'ish-core/facades/product-context.facade';
import { ProductView } from 'ish-core/models/product-view/product-view.model';
import { whenTruthy } from 'ish-core/utils/operators';

import { CustomQuickorderFacade } from '../../../facades/custom-quickorder.facade';
import {
  QuickOrder,
  QuickOrderItem,
  UpdateQuickOrderItem,
} from '../../../models/custom-quickorder/custom-quickorder.model';

/**
 * The Quick Order Repeat Component provides a formly field element, which displays a field array of n elements depending on the according model.
 * Each line displays a form field for the sku and for the according quantity.
 * The component handles adding and removing of elements to the according formly model.
 * The component controls the product contexts for a proper behavior between entered product sku and its quantity field.
 */
@Component({
  selector: 'custom-quickorder-repeat-field',
  templateUrl: './custom-quickorder-repeat-field.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomQuickorderRepeatFieldComponent extends FieldArrayType implements AfterViewInit, OnInit, OnDestroy {
  @ViewChildren(ProductContextDirective) contexts: QueryList<{ context: ProductContextFacade }>;
  @ViewChildren('quickOrderItemRow') quickOrderItemRows: QueryList<ElementRef>;

  deleteModelItems: string[] = [];
  private destroy$ = new Subject<void>();
  quickOrder$: Observable<QuickOrder>;
  loading$: Observable<boolean>;
  freeFreightThreshold$: Observable<number>;
  lastUpdatedSKUIndex = -1;
  quantityUpdatedLineItemID = '';
  basketMaxSize$: Observable<number>;
  basketMaxSize: number;
  isSelectAll = false;
  isDeleteButtonDesabled = true;
  preventFocus = true;

  constructor(
    private cdRef: ChangeDetectorRef,
    private quickOrderFacade: CustomQuickorderFacade,
    private appFacade: AppFacade
  ) {
    super();
  }

  openQuickView(productSku: string) {
    this.appFacade.openQuickView(productSku);
  }

  ngOnInit(): void {
    this.quickOrder$ = this.quickOrderFacade.quickOrder$;
    this.loading$ = this.quickOrderFacade.quickOrderLoading$;
    this.basketMaxSize$ = this.quickOrderFacade.basketMaxItemSize$;
    this.freeFreightThreshold$ = this.quickOrderFacade.customerFreeFreightThreshold$;
  }

  ngAfterViewInit() {
    this.quickOrder$
      .pipe(whenTruthy(), distinctUntilChanged(isEqual), takeUntil(this.destroy$))
      .subscribe(quickOrderData => {
        this.cdRef.detectChanges();
        const quickOrderItems = quickOrderData.items;
        const highestPosition = quickOrderItems.length > 0 ? quickOrderItems[quickOrderItems.length - 1].position : 0;
        const additionalRows = this.getAdditionalRowsToBeAdded(highestPosition);

        this.removePreviousModel();

        this.addItemsAccordingToThePositions(this.getPositionItemMap(quickOrderItems), highestPosition);
        this.addAdditionalItems(highestPosition, additionalRows);

        this.cdRef.markForCheck();

        this.updateContexts('initial', 0);
        if (!this.preventFocus && !quickOrderData.errors) {
          this.setInputFocus(quickOrderData);
        }
        this.preventFocus = false;
      });
  }

  removePreviousModel() {
    const previousModelItemsLength = this.model.length;
    for (let i = 0; i < previousModelItemsLength; i++) {
      this.remove(0);
    }
  }

  getAdditionalRowsToBeAdded(highestPosition: number): number {
    const previousModelItemsLength = this.model.length;
    let additionalRows = this.props.numberMoreRows - highestPosition;
    if (additionalRows < 0) {
      const rowsOccupied = highestPosition % this.props.numberMoreRows;
      if (!(rowsOccupied === 0)) additionalRows = this.props.numberMoreRows - rowsOccupied;
      else additionalRows = 0;
    }
    additionalRows =
      highestPosition + additionalRows >= previousModelItemsLength
        ? highestPosition + additionalRows
        : previousModelItemsLength;
    return additionalRows;
  }

  getPositionItemMap(quickOrderItems: QuickOrderItem[]): Map<number, QuickOrderItem> {
    const positionItemMap = new Map<number, QuickOrderItem>();
    quickOrderItems.forEach(quickOrderItem => {
      positionItemMap.set(quickOrderItem.position, quickOrderItem);
    });
    return positionItemMap;
  }

  addItemsAccordingToThePositions(positionItemMap: Map<number, QuickOrderItem>, highestPosition: number) {
    for (let i = 0; i < highestPosition; i++) {
      if (positionItemMap.has(i + 1)) {
        this.add(i, {
          itemLineId: positionItemMap.get(i + 1).itemLineId,
          productSKU: positionItemMap.get(i + 1).productSKU,
          productName: positionItemMap.get(i + 1).productName,
          quantity: positionItemMap.get(i + 1).quantity,
          eachPrice: positionItemMap.get(i + 1).eachPrice,
          totalPrice: positionItemMap.get(i + 1).totalPrice,
          selected: false,
        });
      } else {
        this.add(i, { productSKU: '', quantity: 1 });
      }
    }
    this.cdRef.detectChanges();
  }

  addAdditionalItems(highestPosition: number, additionalRows: number) {
    for (let i = highestPosition; i < additionalRows; i++) {
      this.add(i, { productSKU: '', quantity: 1 });
    }
  }
  setInputFocus(quickOrderData: QuickOrder) {
    this.cdRef.detectChanges();

    let inputFocusPosition = 0;
    const nextPositionOfQuantityUpdated = this.getNextPositionOfQuantityUpdatedSKU(quickOrderData.items);
    if (nextPositionOfQuantityUpdated !== -1) {
      inputFocusPosition = nextPositionOfQuantityUpdated;
    } else {
      inputFocusPosition = quickOrderData.noOfItems;
    }
    this.cdRef.markForCheck();
    this.quickOrderItemRows.forEach((item, index) => {
      const inputSKUElement = item.nativeElement.querySelector('input') as HTMLInputElement;
      if (index === inputFocusPosition) inputSKUElement.focus();
    });
  }

  resetUpdatedSKUIndexes() {
    this.lastUpdatedSKUIndex = -1;
    this.quantityUpdatedLineItemID = '';
  }

  getNextPositionOfQuantityUpdatedSKU(quickOrderItems: QuickOrderItem[]): number {
    if (this.quantityUpdatedLineItemID.length > 0) {
      const positionUpdated = quickOrderItems
        .filter(item => item.itemLineId === this.quantityUpdatedLineItemID)
        .map(item => item.position);
      return positionUpdated[0];
    }
    return -1;
  }

  updateQuantityUpdatedLineItemID(itemLineID: string) {
    this.quantityUpdatedLineItemID = itemLineID;
    this.lastUpdatedSKUIndex = -1;
  }

  addMultipleRows(rows: number) {
    this.basketMaxSize$
      .pipe(whenTruthy(), take(1), takeUntil(this.destroy$))
      .subscribe(basketMaxSize => (this.basketMaxSize = basketMaxSize));

    if (this.basketMaxSize - this.model.length < 10) {
      rows = this.basketMaxSize - this.model.length;
    }
    for (let i = 0; i < rows; i++) {
      this.add(this.model.length, { productSKU: '', quantity: 1 });
    }
    const contextStartIndex = this.model.length - rows;
    this.updateContexts('additional', contextStartIndex);
  }

  selectAll() {
    let isItemPresent = false;
    this.model.forEach((item: QuickOrderItem) => {
      if (item.itemLineId) {
        item.selected = !this.isSelectAll;
        isItemPresent = true;
      }
    });

    if (isItemPresent) {
      this.isSelectAll = !this.isSelectAll;
      this.isDeleteButtonDesabled = !this.isSelectAll;
    }
  }
  disableDeleteButton() {
    let count = 0;
    this.model.forEach((item: QuickOrderItem) => {
      if (item.selected) {
        count++;
      }
    });
    if (count > 0) {
      this.isDeleteButtonDesabled = false;
      return;
    }
    this.isDeleteButtonDesabled = true;
  }
  DeleteModelItems() {
    const deleteModel: string[] = [];
    this.model.forEach((element: QuickOrderItem) => {
      if (element.selected) {
        deleteModel.push(element.itemLineId);
      }
    });
    if (deleteModel.length > 0) {
      this.quickOrderFacade.deleteQuickOrderItems(deleteModel);
      this.resetUpdatedSKUIndexes();
      this.isDeleteButtonDesabled = true;
      this.isSelectAll = false;
    }
  }

  /**
   * Set the form control field to the according product context and handle its behavior.
   */
  updateContexts(rowTypes: string, rowsToStart: number) {
    this.cdRef.detectChanges();

    this.contexts.forEach((context: { context: ProductContextFacade }, index) => {
      if (index < rowsToStart) return;

      const field = this.field.fieldGroup[index].fieldGroup[0];
      const formControl = field.formControl;

      if (formControl.value) {
        context.context.set('sku', () => formControl.value);
      }

      this.connectFormControlToSku(context, formControl);
      this.setLoadingOnValueChange(context, formControl);

      formControl.setAsyncValidators(() => this.asyncProductValidation(context, formControl, index));

      if (rowTypes === 'initial' || !this.model[index]?.itemLineId) {
        this.bindFormControlToQuickOrderChanges(formControl, context, index);
      }
    });
  }

  private connectFormControlToSku(context: { context: ProductContextFacade }, formControl: AbstractControl) {
    context.context.connect(
      'sku',
      formControl.valueChanges.pipe(
        debounceTime(500),
        tap(value => {
          if (!value) {
            context.context.set('product', this.createEmptyProduct());
          }
        })
      )
    );
  }

  private setLoadingOnValueChange(context: { context: ProductContextFacade }, formControl: AbstractControl) {
    formControl.valueChanges.pipe(map(() => context.context.set('loading', () => true)));
  }

  private asyncProductValidation(
    context: { context: ProductContextFacade },
    formControl: AbstractControl,
    index: number
  ) {
    return context.context.select('product').pipe(
      withLatestFrom(this.quickOrder$),
      map(([product, quickorder]) => {
        this.cdRef.markForCheck();

        if (!product) return;

        const quickorderSkus = quickorder.items.reduce((acc, item) => {
          acc[item.position - 1] = item.productSKU;
          return acc;
        }, new Array<string>(quickorder.items.length));

        if (product.sku && !product.available) return { validProduct: false };

        if (!product.sku) {
          formControl.valueChanges.pipe(map(() => formControl.setErrors(undefined)));
          return;
        }

        const isExistingProduct = quickorderSkus.includes(product.sku) && quickorderSkus.indexOf(product.sku) !== index;

        return product.failed && formControl.value.trim() !== ''
          ? { validProduct: false }
          : isExistingProduct
          ? { existingProduct: false }
          : undefined;
      })
    );
  }

  private createEmptyProduct(): () => ProductView {
    return () => ({
      defaultCategory: undefined,
      failed: false,
      sku: undefined,
    });
  }

  bindFormControlToQuickOrderChanges(
    formControl: AbstractControl,
    context: { context: ProductContextFacade },
    index: number
  ) {
    combineLatest([formControl.valueChanges, context.context.select('product')])
      .pipe(debounceTime(500), withLatestFrom(this.quickOrder$), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(([[sku, product], quickorder]) => {
        const quickorderSkus = quickorder.items.reduce((acc, item) => {
          acc[item.position - 1] = item.productSKU;
          return acc;
        }, new Array<string>(quickorder.items.length));
        const isProductInQuickOrder = quickorderSkus.includes(product.sku);
        const isIndexMatch = quickorderSkus.indexOf(product.sku) !== index;
        const isExistingProduct = isProductInQuickOrder && isIndexMatch;
        if (isExistingProduct) context.context.set('productIndex', () => index);

        const shouldCreateOrUpdate = !isExistingProduct && !product.failed && sku === product.sku && product.available;
        if (shouldCreateOrUpdate) {
          this.createOrUpdateQuickOrderItem(index, product, quickorderSkus, quickorder.items);
        } else {
          this.handleFailedOrRemoveQuickOrderItem(index, product);
        }
      });
  }

  private createOrUpdateQuickOrderItem(
    index: number,
    product: ProductView,
    quickorderSkus: string[],
    quickorderItems: QuickOrderItem[]
  ) {
    const quickOrderItem: UpdateQuickOrderItem = {
      productSKU: product.sku,
      quantity: isNaN(this.model[index].quantity) ? 1 : this.model[index].quantity,
    };
    if (!this.model[index].itemLineId) {
      quickOrderItem.position = index + 1;
      this.setUpdatedProductIndex(index);
      this.quickOrderFacade.createQuickOrderItems([quickOrderItem]);
    } else if (quickorderSkus.indexOf(product.sku) === index) {
      this.updateModelWithSelectedProduct(index, product.sku, quickorderItems);
    } else {
      this.setUpdatedProductIndex(index);
      this.quickOrderFacade.updateQuickOrderItem(this.model[index].itemLineId, quickOrderItem);
    }

    this.cdRef.markForCheck();
  }

  private updateModelWithSelectedProduct(index: number, productSku: string, quickorderItems: QuickOrderItem[]) {
    const selectedProduct = quickorderItems.find(item => productSku === item.productSKU);
    if (selectedProduct) {
      this.model[index] = {
        itemLineId: selectedProduct.itemLineId,
        productSKU: selectedProduct.productSKU,
        productName: selectedProduct.productName,
        quantity: selectedProduct.quantity,
        eachPrice: selectedProduct.eachPrice,
        totalPrice: selectedProduct.totalPrice,
        selected: false,
      };
    }
  }

  private handleFailedOrRemoveQuickOrderItem(index: number, product: ProductView) {
    const indexProductToBeRemoved = this.model.findIndex(
      (item: QuickOrderItem) => item.productSKU === '' && !!item.itemLineId
    );
    const isExistingProduct =
      this.model[index]?.productName !== product.name && this.model[index]?.productSKU === product.sku;
    if (indexProductToBeRemoved > -1) {
      const itemToDelete = this.model[indexProductToBeRemoved].itemLineId;
      this.quickOrderFacade.deleteQuickOrderItems([itemToDelete]);
      this.resetUpdatedSKUIndexes();
    } else if (product.failed || isExistingProduct) {
      this.model[index] = {
        itemLineId: this.model[index].itemLineId,
        productSKU: product.sku,
        quantity: 1,
      };
      this.cdRef.detectChanges();
    }
  }

  setUpdatedProductIndex(index: number) {
    this.lastUpdatedSKUIndex = index;
    this.quantityUpdatedLineItemID = '';
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
